"use client";

import React, { useState, useEffect, useRef } from "react";
import type { ReactElement } from "react";
import type { LifeRebuildScoreAnalysisResponse } from "./types";
import { LIFE_REBUILD_REPORT_PRICE_USD } from "./report-types";
import { InlineReportOffer, type ReportCheckoutButtonProps } from "./inline-report-offer";

// Props provided by the consuming app (StepByStepAssessment). Mirrors the
// personalized-goal-plan result page contract: the app injects UI primitives,
// icons, and the posthog instance so this stays decoupled from next/lucide.
export interface LifeRebuildResultPageProps {
  runId: string;
  publicAccessToken: string;
  email?: string;
  assessmentTitle?: string;
  assessmentType?: string;
  isTestMode?: boolean;
  isGated?: boolean;
  testResultsType?: "generic" | "ai";
  isComplete: boolean;
  analysisResult: LifeRebuildScoreAnalysisResponse | { analysis: string } | null;
  posthog?: {
    capture: (event: string, properties?: Record<string, unknown>) => void;
  };
  components: {
    // Opens the $19 report checkout in place (Stripe lives in the app, not here).
    ReportCheckoutButton: React.ComponentType<ReportCheckoutButtonProps>;
    LoadingSpinner?: React.ComponentType;
    icons: {
      CheckCircle: React.ComponentType<{ className?: string }>;
      ChevronDown: React.ComponentType<{ className?: string }>;
      Compass: React.ComponentType<{ className?: string }>;
      Sparkles: React.ComponentType<{ className?: string }>;
    };
  };
}

export function LifeRebuildScoreResultPage({
  runId,
  email,
  assessmentType,
  isComplete,
  analysisResult,
  posthog,
  components,
}: LifeRebuildResultPageProps): ReactElement {
  const { LoadingSpinner, ReportCheckoutButton, icons } = components;
  const { CheckCircle, ChevronDown, Compass, Sparkles } = icons;
  const hasTrackedResultView = useRef(false);

  // The sticky buy bar waits until the reader has scrolled past the score.
  const heroRef = useRef<HTMLDivElement>(null);
  const [isPastHero, setIsPastHero] = useState(false);
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => setIsPastHero(!entry.isIntersecting));
    observer.observe(hero);
    return () => observer.disconnect();
  }, [isComplete]);

  // Event name kept from the lock-card page so the result-viewer count stays comparable.
  useEffect(() => {
    if (isComplete && analysisResult && !hasTrackedResultView.current) {
      hasTrackedResultView.current = true;
      if (posthog && typeof posthog.capture === "function") {
        posthog.capture("minitools_locked_section_viewed", {
          minitools_assessment_type: assessmentType,
          run_id: runId,
        });
      }
    }
  }, [isComplete, analysisResult, assessmentType, runId, posthog]);

  if (!isComplete) {
    if (LoadingSpinner) {
      return <LoadingSpinner />;
    }
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4" />
          <p className="text-gray-600">Reading your answers across the six domains...</p>
        </div>
      </div>
    );
  }

  // Resolve the analysis into the structured archetype shape, tolerating both the
  // typed object (happy path) and a raw-markdown / StandardAnalysis fallback.
  const data: LifeRebuildScoreAnalysisResponse = (() => {
    if (!analysisResult) {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { parseLifeRebuildAnalysis } = require("./types");
      return parseLifeRebuildAnalysis("");
    }

    if ("domains" in analysisResult && Array.isArray((analysisResult as LifeRebuildScoreAnalysisResponse).domains)) {
      return analysisResult as LifeRebuildScoreAnalysisResponse;
    }

    if ("analysis" in analysisResult) {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { parseLifeRebuildAnalysis } = require("./types");
      return parseLifeRebuildAnalysis((analysisResult as { analysis: string }).analysis || "");
    }

    // StandardAnalysis-shaped fallback (category / insights / score, no domains).
    const std = analysisResult as unknown as {
      category?: string;
      score?: number;
      insights?: string[];
    };
    return {
      category: std.category || "Your Life Rebuild",
      score: typeof std.score === "number" ? std.score : 50,
      diagnosis:
        "Your read across the six domains is ready below, with the one place to start first.",
      domains: (std.insights || []).slice(0, 6).map((read) => ({ domain: "", read })),
      insights: std.insights || [],
      rebuildFirst: {
        move: "Defend one anchor routine for fourteen days before touching anything else.",
        detail:
          "Pick the single rhythm that steadies the rest of your week and protect it first. One system at a time, in the right order.",
      },
      whatComesNext:
        "Your full rebuild sequence and a day-by-day first week are ready. Unlock them to start the rebuild in the right order.",
    };
  })();

  const readinessScore = Number.isFinite(data.score)
    ? Math.min(100, Math.max(0, Math.round(data.score)))
    : 50;
  const freePatternInsight = data.insights.find((insight) => insight.trim().length > 0);

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* ─── Completion Hero ─── */}
      <div ref={heroRef} className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-6 py-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-6">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <p className="text-sm uppercase tracking-wide text-emerald-700 font-semibold mb-2">
            Your Life Rebuild Score
          </p>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            You are in {data.category}
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto mb-2">
            {data.diagnosis}
          </p>
          <div className="mt-6 mx-auto max-w-md rounded-2xl border border-emerald-100 bg-emerald-50/70 px-5 py-4 text-left">
            <div className="flex items-end justify-between gap-4 mb-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">
                  Rebuild readiness
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Your current foundation across all six domains
                </p>
              </div>
              <p className="text-3xl font-bold text-gray-900 whitespace-nowrap">
                {readinessScore}<span className="text-base font-medium text-gray-500"> / 100</span>
              </p>
            </div>
            <div
              className="h-2.5 rounded-full overflow-hidden bg-white"
              role="progressbar"
              aria-label="Life Rebuild readiness score"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={readinessScore}
            >
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${readinessScore}%`, backgroundColor: "#2A6354" }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              This is a starting point, not a grade or a measure of your worth.
            </p>
          </div>
          {email && (
            <p className="mt-4 text-sm text-gray-600">
              We&apos;re also emailing your score to <span className="font-medium text-gray-900">{email}</span>
            </p>
          )}
          <div style={{ animation: "bounceDown 1.5s ease-in-out infinite" }} className="mt-4">
            <ChevronDown className="w-6 h-6 mx-auto text-gray-400" />
          </div>
          <style>{`
            @keyframes bounceDown {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(8px); }
            }
          `}</style>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 pt-8 pb-6 space-y-8">
        {/* Six-domain read (FREE) */}
        <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-6 md:p-8">
          <div className="flex items-start gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <Compass className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <p className="text-sm uppercase tracking-wide text-emerald-700 font-semibold">
                Your rebuild snapshot
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Where pressure is showing up now. This is a map, not the order to tackle it.
              </p>
            </div>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {data.domains.length > 0 ? (
              data.domains.map((d, index) => (
                <li key={index} className="flex items-start gap-3 rounded-xl bg-gray-50 border border-gray-100 p-4">
                  <span
                    className="mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: "#2A6354" }}
                    aria-hidden="true"
                  />
                  <div>
                    {d.domain && (
                      <p className="text-sm font-semibold text-gray-900">{d.domain}</p>
                    )}
                    <p className="text-sm text-gray-700">{d.read}</p>
                  </div>
                </li>
              ))
            ) : (
              <li className="text-gray-500 text-sm">
                Your six-domain read is being prepared.
              </li>
            )}
          </ul>
        </div>

        {freePatternInsight && (
          <div className="rounded-2xl p-6 md:p-8 text-white" style={{ backgroundColor: "#2A6354" }}>
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-amber-300 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm uppercase tracking-wide text-emerald-100 font-semibold mb-2">
                  The pattern underneath
                </p>
                <p className="text-lg md:text-xl leading-relaxed text-white">
                  {freePatternInsight}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* The free first move stays, smaller, so it reads as the start rather than the answer. */}
        <div className="bg-white rounded-2xl border border-gray-200 px-6 py-5">
          <p className="text-sm uppercase tracking-wide text-amber-800 font-semibold mb-1">
            Start here, today
          </p>
          <p className="text-base md:text-lg font-semibold text-gray-900">{data.rebuildFirst.move}</p>
          <p className="text-sm text-gray-600 mt-2">
            Your report puts this in order with the other five domains.
          </p>
        </div>

        <InlineReportOffer runId={runId} email={email} ReportCheckoutButton={ReportCheckoutButton} />
      </div>

      {isPastHero && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
          <div className="max-w-xl mx-auto px-4 py-3">
            <ReportCheckoutButton
              runId={runId}
              email={email}
              assessmentType="life-rebuild-score"
              ctaLocation="sticky_bottom"
              className="w-full min-h-[48px] rounded-lg px-6 text-base font-bold text-white bg-[#2A6354] hover:bg-[#1f4a3f] transition-colors disabled:opacity-60"
            >
              {`Get My Rebuild Plan · $${LIFE_REBUILD_REPORT_PRICE_USD}`}
            </ReportCheckoutButton>
            <p className="text-xs text-gray-600 text-center mt-2">30-day money-back guarantee</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default LifeRebuildScoreResultPage;
